<template>
  <div :class="['relative', mounted && 'relative--mounted']">
    <portal to="search" :disabled="!fullscreen">
      <transition name="v-searchable-select--fullscreen">
        <div
          :class="[
            'v-searchable-select',
            small && 'v-searchable-select--small',
            gray && 'v-searchable-select--gray',
            search && 'v-searchable-select--search',
            url && 'v-searchable-select--async',
            url && showResults && 'v-searchable-select--show-results',
            showError && !!error && 'v-searchable-select--error',
            fullscreen && 'v-searchable-select--fullscreen',
            desktopFullscreen && 'v-searchable-select--desktopFullscreen',
            disabled && 'v-searchable-select--disabled'
          ]"
          :style="[minWidth && { minWidth: `${minWidth}px` }]"
          v-show="show"
          @keydown.esc="closeFullscreen()"
          @click.self="closeFullscreen()"
        >
          <div class="v-searchable-select__wrapper">
            <multiselect
              v-model="selectedValue"
              v-bind="!multiple ? selectProps : multiselectProps"
              :options="internalOptions"
              :options-limit="2000"
              :placeholder="placeholder"
              :label="label"
              :track-by="optionId"
              :group-values="getGroupValues"
              :group-label="getGroupLabel"
              :internalSearch="!url"
              :loading="loading"
              :option-height="optionHeight"
              :disabled="disabled"
              :multiple="multiple"
              @open="onOpen"
              @close="onClose"
              @input="onInput"
              @select="onSelect"
              @search-change="onSearchChange"
              spellcheck="false"
              ref="multiselect"
              :preserve-search="this.url ? true : false"
              :clear-on-select="this.url ? false : true"
            >
              <template #option="{option}">
                <template v-if="customResult && !option.$isLabel">
                  <slot v-bind:result="option" name="custom-result"></slot>
                </template>
                <span v-else-if="option" class="multiselect__standard-option">
                  {{ getOptionLabel(option) }}
                  <span v-if="metaLabel">{{ option[metaLabel] }}</span>
                </span>
              </template>
              <template #caret="{toggle}">
                <div
                  v-show="!url && !fullscreen"
                  :class="['multiselect__caret', { 'mr-5': !!tooltip }]"
                  @mousedown.prevent.stop="toggle()"
                >
                  <v-icon name="caret" width="12" />
                </div>
                <div v-show="search" class="multiselect__search-icon">
                  <v-icon name="search" width="22" />
                </div>
              </template>
              <template #noResult>
                <span class="multiselect__no-results">
                  <slot v-if="customNoResult" name="custom-no-result"></slot>
                  <template v-else>{{
                    $t(
                      "Your search returned no results",
                      "components.searchable-select.no-results"
                    )
                  }}</template>
                </span>
              </template>
              <template #loading>
                <div>
                  <v-loading
                    v-if="fetching"
                    :small="small"
                    :medium="!small"
                  ></v-loading>
                  <div
                    v-if="
                      (!fetching && query) ||
                        (!fetching && selectedValue && url) ||
                        (clearButton && selectedValue)
                    "
                    @click.prevent.stop="resetSearch()"
                    class="v-searchable-select__clear"
                  >
                    <div class="v-searchable-select__clear__icon">
                      <v-icon name="x" width="8" />
                    </div>
                  </div>
                  <div
                    v-if="fullscreen"
                    class="v-searchable-select--fullscreen__close"
                    @mousedown.prevent.stop="closeFullscreen()"
                  >
                    {{ $t("Luk", "components.searchable-select.close") }}
                  </div>
                </div>
              </template>
              <template v-if="hasFallback" #afterList>
                <div class="v-searchable-select__after-list">
                  <slot
                    name="fallback"
                    :closeFullscreen="closeFullscreen"
                  ></slot>
                </div>
              </template>
            </multiselect>
            <div
              v-if="placeholder"
              :class="[
                'v-searchable-select__wrapper__label',
                {
                  'v-searchable-select__wrapper__label--active':
                    selectedValue || address,
                  'v-searchable-select__wrapper__label--error': showError,
                  'v-searchable-select__wrapper__label__search':
                    search && !desktopFullscreen,
                  'v-searchable-select__wrapper__label__search__desktopFullscreen': desktopFullscreen
                }
              ]"
            >
              <span
                :class="[
                  'v-searchable-select__wrapper__label__text',
                  {
                    'v-searchable-select__wrapper__label__text--active':
                      selectedValue || address
                  }
                ]"
              >
                {{ placeholder }}
              </span>
            </div>
            <span
              v-if="address && !selectedValue"
              :class="['v-searchable-select__wrapper__address-value']"
            >
              {{ formatAddress }}</span
            >
          </div>
          <input
            class="v-searchable-select__input"
            type="text"
            :value="selectedValue && getSelectedValue()"
            v-bind="$attrs"
            @invalid="onInvalid"
            ref="input"
          />
          <div
            v-if="showError || description"
            class="v-searchable-select__helper"
          >
            {{ showError ? error : description }}
          </div>
        </div>
      </transition>
    </portal>
    <!-- Use shallow component on mobile to trigger fullscreen view -->
    <v-searchable-select-shallow
      v-if="mobile && !desktopFullscreen"
      :placeholder="placeholder"
      :label="label"
      :name="$attrs['name']"
      :required="$attrs.required"
      :value="selectedValue"
      @input="newVal => (selectedValue = newVal)"
      :async="!!url"
      :small="small"
      :gray="gray"
      :search="search"
      :error="error"
      :show-error="showError"
      @click="openFullscreen"
      :disabled="disabled"
      :tooltip="tooltip"
      :address="formatAddress"
      :multiple="multiple"
    />
    <v-infoicon v-if="tooltip" :tooltip="tooltip" offset="16px"></v-infoicon>
  </div>
</template>

<script>
// Dependencies
import Multiselect from "vue-multiselect";
import vSearchableSelectShallow from "./v-searchable-select-shallow";

// Mixins
import { default as resetMixin } from "../v-form/mixin.reset";
import { default as propsMixin } from "./mixins/props";
import { default as dataMixin } from "./mixins/data";
import { default as eventsMixin } from "./mixins/events";
import { default as searchMixin } from "./mixins/search";
import { default as mobileMixin } from "./mixins/mobile";

export default {
  inheritAttrs: false,
  mixins: [
    resetMixin,
    propsMixin,
    dataMixin,
    eventsMixin,
    searchMixin,
    mobileMixin
  ],
  components: {
    Multiselect,
    "v-searchable-select-shallow": vSearchableSelectShallow
  },
  watch: {
    value(value) {
      this.selectedValue = this.transformValue(value);
      if ((value && value.length == 0) || value == null) {
        this.selectedValue = null;
      }
    },
    getUrl() {
      this.updateUrl(this.url);
      this.clearSelectedValue();
    }
  },
  created() {
    this.showError = this.invalid !== false;
  },
  mounted() {
    setTimeout(() => this.fixInputElement(), 0);
    if (this.desktopFullscreen) {
      this.$root.$on(this.fullscreenTrigger, this.openFullscreen);
    }
  },
  beforeDestroy() {
    if (this.desktopFullscreen) {
      this.$root.$off(this.fullscreenTrigger, this.openFullscreen);
    }
  },
  computed: {
    hasFallback() {
      return !!this.$slots.fallback;
    },
    getUrl() {
      return this.url;
    }
  },
  methods: {
    openFullscreen() {
      setTimeout(() => this.multiselect().search.focus(), 0);
      if (this.mobile) this.openMobile();
      this.fullscreenOpen = true;
    },
    closeFullscreen() {
      setTimeout(() => this.multiselect().search.blur(), 0);
      if (this.mobile) this.closeMobile();
      this.fullscreenOpen = false;
    },
    getSelectedValue() {
      return this.selectedValue.value || this.selectedValue;
    },
    transformValue(value) {
      if (this.simpleSelect) {
        return this.options.find(option => option.value == value);
      } else {
        return value;
      }
    },
    getOptionLabel(option) {
      if (option.$isLabel) return option.$groupLabel;
      return option[this.label] || option || "";
    },
    fixInputElement() {
      const inputElement = this.multiselect().search;
      inputElement.setAttribute("autocorrect", "off");
      inputElement.setAttribute("autocapitalize", "off");

      if (this.placeholder) {
        inputElement.setAttribute("aria-label", this.placeholder);
      }
    },
    deactivate() {
      if (this.fullscreen) this.closeFullscreen();
      this.$refs.multiselect.deactivate();
    },
    triggerSearch(query) {
      this.$refs.multiselect.updateSearch(query);
    },
    updateUrl(url) {
      this.internalUrl = url;
    },
    setSearchDisabled(value) {
      this.searchDisabled = value;
    },
    multiselect() {
      return this.$refs.multiselect ? this.$refs.multiselect.$refs : null;
    },
    clearSelectedValue() {
      this.selectedValue = null;
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped lang="scss">
@import "../../sass/component.scss";

.relative {
  position: relative;
  visibility: hidden;

  &--mounted {
    visibility: visible;
  }
}

.v-searchable-select {
  &__wrapper {
    position: relative;

    &__address-value {
      display: inline-block;
      position: absolute;
      top: 13px;
      left: 10px;
      right: auto;
      bottom: auto;
      max-width: 100%;
      color: theme-color("black");
      background: transparent;
      padding: 0 5px;
      transform-origin: left top;
      transition: all 200ms ease-out;
      cursor: text;
      pointer-events: none;
      white-space: nowrap;
    }

    @include media-breakpoint-up(md) {
      &__label {
        display: inline-block;
        position: absolute;
        top: 13px;
        left: 10px;
        right: auto;
        bottom: auto;
        max-width: 100%;
        color: theme-color("placeholder");
        background: transparent;
        padding: 0 5px;
        transform-origin: left top;
        transition: all 200ms ease-out;
        cursor: text;
        pointer-events: none;
        white-space: nowrap;
        line-height: 24px;

        &__search {
          top: 16px;
          left: $spacer * 3;

          .multiselect--active + & .v-searchable-select__wrapper__label__text {
            position: relative;
            top: -12px;

            &--error {
              color: theme-color("danger");
            }
          }

          &__text {
            &--active {
              position: relative;
              top: -12px;
            }
          }

          &__desktopFullscreen {
            top: 26px;
            left: $spacer * 3;
          }
        }

        .multiselect--active + & .v-searchable-select__wrapper__label__text {
          position: relative;
          top: -12px;

          &--error {
            color: theme-color("danger");
          }
        }

        &__text {
          &--active {
            position: relative;
            top: -12px;
          }
        }

        .multiselect--active + & {
          background-color: theme-color("white");
          top: 0px;
          z-index: 49;
          font-size: 0.8125rem;
          color: theme-color("focus");
          height: 5px;

          &--error {
            color: theme-color("danger");
          }
        }

        &__search__desktopFullscreen {
          display: none;
        }
        &--active {
          background-color: theme-color("white");
          top: 0px;
          z-index: 47;
          font-size: 0.8125rem;
          height: 5px;
        }

        &--error {
          color: theme-color("danger");
        }

        .v-searchable-select--gray & {
          background-color: inherit;
        }
      }
    }
  }
  &__input {
    display: none;
  }

  &__close {
    display: none;
  }

  &__clear {
    position: absolute;
    right: 5px;
    top: 3px;
    padding: 12px 10px 10px 10px;
    z-index: 10;

    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      color: theme-color("white");
      background-color: theme-color("border");
      cursor: pointer;
      transition: background-color 200ms ease-out;
      border-radius: 50%;

      &:hover {
        background-color: theme-color("border");
        filter: brightness(0.9);
      }
    }
  }

  &__helper {
    display: none;

    @include media-breakpoint-up(md) {
      display: block;
      margin-top: 5px;
      font-size: 0.75rem;
      color: theme-color("placeholder");
    }
  }

  &__after-list {
    position: sticky;
    bottom: 0;
    background-color: theme-color("white");
    padding: 11px 0;
    border-top: 1px solid #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &--disabled {
    cursor: not-allowed;
  }
}

// Multiselect deep styling for different component states
@import "./styles/base.scss";
@import "./styles/small.scss";
@import "./styles/gray.scss";
@import "./styles/search.scss";
@import "./styles/async.scss";
@import "./styles/error.scss";
@import "./styles/fullscreen.scss";
</style>
